import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import StatsControls from "./Controls";
import EnteredChart from "./EnteredChart";

import CarriersChart from "./CarriersChart";
import ChannelsChart from "./ChannelsChart";
import TopMarginChart from "./TopMarginChart";
import TopRelationChart from "./TopRelationChart";

import BackdropLoader from "../../../../components/BackdropLoader";
import RelationStatsSkeleton from "../../../../components/skeletons/RelationStatsSkeleton";
import { useGetCurrentCompanyBranchesQuery } from "../../../branches/branchesApiSlice";
import { useGetCurrentUserQuery } from "../../../user/userApiSlice";
import { useGetCompanyRelationStatsQuery } from "../../statisticsApiSlice";

const today = new Date();

const RelationStatistics = () => {
	const { data: user } = useGetCurrentUserQuery();
	const isBranchManager = user?.role === "branch-manager";

	const {
		data: branches,
		error: branchesError,
		isLoading: isLoadingBranches,
	} = useGetCurrentCompanyBranchesQuery();

	const [branchValue, setBranchValue] = useState(
		isBranchManager ? user?.company_branch_id : ""
	);
	const onBranchChange = (e) => {
		e.preventDefault();
		setBranchValue(e.target.value);
	};

	const [rangeValue, setRangeValue] = useState([today, today]);

	const [weightValues, setWeightValues] = useState({ from: "", to: "" });
	const onWeightChange = (e, type) => {
		e.preventDefault();

		if (type === "from") {
			setWeightValues({ ...weightValues, from: e.target.value });
		} else {
			setWeightValues({ ...weightValues, to: e.target.value });
		}
	};

	const [filterValues, setFilterValues] = useState({
		branch: isBranchManager ? user?.company_branch_id : "",
		range: rangeValue.map((date) => {
			const formatter = new Intl.DateTimeFormat("en", {
				month: "2-digit",
				year: "numeric",
			});
			const [month, year] = formatter.format(date).split("/");
			return `${month}_${year}`;
		}),
		weightFrom: "",
		weightTo: "",
	});
	const [isRefetching, setIsRefetching] = useState(false);

	const {
		refetch,
		data: stats,
		error,
		isLoading,
	} = useGetCompanyRelationStatsQuery(filterValues);

	const handleSubmit = async () => {
		setIsRefetching(true);
		const parsedRange = rangeValue.map((date) => {
			const formatter = new Intl.DateTimeFormat("en", {
				month: "2-digit",
				year: "numeric",
			});
			const [month, year] = formatter.format(date).split("/");
			return `${month}_${year}`;
		});

		if (
			filterValues.branch === branchValue &&
			filterValues.range === encodeURIComponent(parsedRange) &&
			filterValues.weightFrom === weightValues.from &&
			filterValues.weightTo === weightValues.to
		) {
			await refetch();
			return;
		}

		setFilterValues({
			branch: branchValue,
			range: encodeURIComponent(parsedRange),
			weightFrom: weightValues.from,
			weightTo: weightValues.to,
		});
	};

	useEffect(() => {
		setIsRefetching(false);
	}, [stats, error]);

	if (isLoading || isLoadingBranches) {
		return <RelationStatsSkeleton />;
	}

	return (
		<>
			<Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
				<StatsControls
					disableBranchSelect={isBranchManager}
					isLoading={isRefetching}
					onSubmit={handleSubmit}
					onBranchChange={isBranchManager ? () => {} : onBranchChange}
					branchValue={branchValue}
					branchOptions={branches.map((branch) => {
						return { value: branch.id, label: branch.name };
					})}
					onRangeChange={setRangeValue}
					rangeValue={rangeValue}
					weightFrom={weightValues.from}
					onWeightFromChange={(e) => onWeightChange(e, "from")}
					weightTo={weightValues.to}
					onWeightToChange={(e) => onWeightChange(e, "to")}
				/>
				<Grid container spacing={5}>
					<Grid size={3}>
						<EnteredChart data={stats.cargosData} />
					</Grid>
					<Grid size={3}>
						<TopRelationChart data={stats.cargosData} />
					</Grid>
					<Grid size={3}>
						<TopMarginChart data={stats.cargosData} />
					</Grid>
					<Grid size={3}>
						<Grid container spacing={4}>
							<Grid size={12}>
								<CarriersChart data={stats.carriersData} />
							</Grid>
							<Grid size={12}>
								<ChannelsChart data={stats.channelsData} />
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Box>
			<BackdropLoader isOpen={isRefetching} />
		</>
	);
};

export default RelationStatistics;
